import React, { useMemo } from 'react';
import { useMutation } from 'react-query';
import { Grid } from 'antd';
import { Currencies } from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { formatCurrency } from '@shared/utils/currency';
import { MeratusRevenueSubjectToDaysAccountCodes } from '@shared/utils/meratus';
import { TcTermsWithIncludes } from '@api/utils/getTcTerms';
import { RevenueSubjectToDaysProps } from '@api/models/revenue-subject-to-days';
import EditableTable from '@client/components/EditableTable';
import {
	createRevenueSubjectToDays,
	deleteRevenueSubjectToDays,
	updateRevenueSubjectToDays,
} from '@client/lib/api';
import getMeratusAccountCodeColumn from '@client/utils/meratus/getMeratusAccountCodeColumn';
import useIsMeratusTcOut from '@client/utils/meratus/useIsMeratusTcOut';

const { useBreakpoint } = Grid;

type RevenueSubjectToDaysValues = {
	name: string;
	amount: number;
};

const RevenueItemsTable = ({
	tcTerms,
	refreshFixture,
	currency,
	locked = false,
	fixtureType,
}: {
	tcTerms: TcTermsWithIncludes;
	refreshFixture: () => void;
	currency: Values<typeof Currencies>;
	locked?: boolean;
	fixtureType?: string | null;
}) => {
	const screens = useBreakpoint();
	const isMeratusTcOut = useIsMeratusTcOut(fixtureType);

	const createMutation = useMutation(
		(values: RevenueSubjectToDaysValues) => {
			return createRevenueSubjectToDays(tcTerms.id, values);
		},
		{ onSuccess: refreshFixture },
	);

	const updateMutation = useMutation(
		({ id, values }: { id: number; values: RevenueSubjectToDaysValues }) => {
			return updateRevenueSubjectToDays(tcTerms.id, id, values);
		},
		{ onSuccess: refreshFixture },
	);

	const deleteMutation = useMutation(
		(id: number) => {
			return deleteRevenueSubjectToDays(tcTerms.id, id);
		},
		{ onSuccess: refreshFixture },
	);

	const handleCreateRevenueSubjectToDays = createMutation.mutateAsync;

	const handleUpdateRevenueSubjectToDays = (
		id: number, values: RevenueSubjectToDaysValues,
	) => {
		return updateMutation.mutateAsync({ id, values });
	};

	const handleDeleteRevenueSubjectToDays = (id: number) => {
		return deleteMutation.mutateAsync(id);
	};

	const columns = useMemo(() => [
		{
			dataIndex: 'name',
			title: 'Name',
			editable: true,
			editingProps: {
				type: 'string' as const,
				inputProps: {
					'data-testid': 'revenue-name',
				},
			},
		},
		{
			dataIndex: 'amount',
			title: 'Amount (per day)',
			editable: true,
			editingProps: {
				type: 'currency' as const,
				inputProps: {
					currency,
					addonAfter: 'Per day',
					precision: 3,
				},
			},
			render: (c: number) => {
				if (c == null) {
					return null;
				}

				return formatCurrency(
					c,
					currency,
					{ forceDecimals: true },
				);
			},
		},
		...(isMeratusTcOut ? [
			getMeratusAccountCodeColumn<RevenueSubjectToDaysProps, 'id'>(
				MeratusRevenueSubjectToDaysAccountCodes,
			),
		] : []),
	], [currency, isMeratusTcOut]);

	return (
		<EditableTable
			key={tcTerms.RevenuesSubjectToDays.length}
			iconButtons
			enableDelete={!locked}
			enableEdit={!locked}
			allowAddNew={!locked}
			pagination={false}
			addNewText="Revenues subject to days"
			emptyText="No revenue items yet"
			dataSource={tcTerms.RevenuesSubjectToDays}
			onSave={handleUpdateRevenueSubjectToDays}
			onAddNew={handleCreateRevenueSubjectToDays}
			onDelete={handleDeleteRevenueSubjectToDays}
			keyDataIndex="id"
			useCards={screens.xs}
			// @ts-ignore
			columns={columns}
			addButtonTestId="revenues-add-button"
		/>
	);
};

export default RevenueItemsTable;
